import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const RouteInformation = ({ routeInfo }) => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Info' },
    { id: 'stops', label: 'Stops & Attractions', icon: 'MapPin' },
    { id: 'pricing', label: 'Pricing Details', icon: 'IndianRupee' },
    { id: 'tips', label: 'Travel Tips', icon: 'Lightbulb' },
  ];

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      <div className="border-b border-border overflow-x-auto">
        <div className="flex min-w-max">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex-shrink-0 flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 font-medium text-sm md:text-base transition-all duration-200 ${
                activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name={tab?.icon} size={18} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="p-4 md:p-6 lg:p-8">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-4">
                Route Overview
              </h3>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                {routeInfo?.overview}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div className="space-y-3">
                <h4 className="text-base md:text-lg font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Route" size={20} className="text-primary" />
                  Route Highlights
                </h4>
                <ul className="space-y-2">
                  {routeInfo?.highlights?.map((highlight, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm md:text-base text-foreground">
                      <Icon name="CheckCircle" size={16} className="text-success mt-1 flex-shrink-0" />
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="space-y-3">
                <h4 className="text-base md:text-lg font-semibold text-foreground flex items-center gap-2">
                  <Icon name="AlertCircle" size={20} className="text-warning" />
                  Important Information
                </h4>
                <ul className="space-y-2">
                  {routeInfo?.importantInfo?.map((info, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm md:text-base text-foreground">
                      <Icon name="Info" size={16} className="text-primary mt-1 flex-shrink-0" />
                      <span>{info}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'stops' && (
          <div className="space-y-6">
            <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
              Stops & Attractions Along the Route
            </h3>
            
            <div className="space-y-4">
              {routeInfo?.stops?.map((stop, index) => (
                <div key={index} className="flex gap-4 p-4 bg-muted rounded-lg">
                  <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                    {index + 1}
                  </div>
                  <div className="flex-1 space-y-2">
                    <h4 className="text-base md:text-lg font-semibold text-foreground">
                      {stop?.name}
                    </h4>
                    <p className="text-sm text-muted-foreground">{stop?.description}</p>
                    <div className="flex flex-wrap gap-2">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Icon name="Clock" size={14} />
                        <span>{stop?.duration}</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Icon name="MapPin" size={14} />
                        <span>{stop?.distance}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {activeTab === 'pricing' && (
          <div className="space-y-6">
            <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
              Detailed Pricing Breakdown
            </h3>
            
            <div className="space-y-4">
              {routeInfo?.pricingDetails?.map((detail, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <Icon name={detail?.icon} size={20} className="text-primary" />
                    <div>
                      <p className="text-sm md:text-base font-medium text-foreground">{detail?.label}</p>
                      <p className="text-xs text-muted-foreground">{detail?.description}</p>
                    </div>
                  </div>
                  <p className="text-base md:text-lg font-semibold text-foreground whitespace-nowrap">
                    ₹{detail?.amount?.toLocaleString('en-IN')}
                  </p>
                </div>
              ))}
              
              <div className="flex items-center justify-between p-4 bg-primary text-primary-foreground rounded-lg">
                <p className="text-base md:text-lg font-semibold">Total Estimated Cost</p>
                <p className="text-xl md:text-2xl font-bold whitespace-nowrap">
                  ₹{routeInfo?.totalCost?.toLocaleString('en-IN')}
                </p>
              </div>
            </div>
            
            <div className="p-4 bg-accent/10 border border-accent rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-1" />
                <p className="text-sm text-foreground">
                  Prices may vary based on season, vehicle type, and booking time. Contact us for exact quotes and special offers.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'tips' && (
          <div className="space-y-6">
            <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
              Travel Tips & Recommendations
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {routeInfo?.travelTips?.map((tip, index) => (
                <div key={index} className="p-4 bg-muted rounded-lg space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name={tip?.icon} size={18} className="text-primary" />
                    </div>
                    <h4 className="text-base md:text-lg font-semibold text-foreground">{tip?.title}</h4>
                  </div>
                  <p className="text-sm text-muted-foreground">{tip?.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RouteInformation;